// Time Analytics Panel Component
import React, { useState, useEffect } from 'react';
import { 
  Clock, Timer, BarChart3, TrendingUp, TrendingDown, 
  Minus, Download, Loader2, PieChart, AlertCircle,
  CheckCircle, XCircle, ArrowRight
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { formatDuration } from './utils';

const TimeAnalyticsPanel = ({ meetingId, meeting, agendaItems, timing, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (isOpen && meetingId) {
      calculateAnalytics();
    }
  }, [isOpen, meetingId, agendaItems, timing]);

  const calculateAnalytics = () => {
    setLoading(true);
    
    // Calculate analytics from the data we have
    const totalMeetingTime = timing?.meeting_elapsed_seconds || 0;
    const generalAdminTime = timing?.general_admin_seconds || 0;
    
    // Calculate time spent on agenda items
    const itemsWithTime = agendaItems.filter(item => item.time_spent_seconds > 0);
    const totalAgendaTime = itemsWithTime.reduce((sum, item) => sum + (item.time_spent_seconds || 0), 0);
    
    // Calculate planned vs actual
    const totalPlannedTime = agendaItems.reduce((sum, item) => sum + ((item.duration_minutes || 0) * 60), 0);
    
    // Calculate variance for each item
    const itemAnalytics = agendaItems.map(item => {
      const planned = (item.duration_minutes || 0) * 60;
      const actual = item.time_spent_seconds || 0;
      const variance = actual - planned;
      const variancePercent = planned > 0 ? ((variance / planned) * 100).toFixed(0) : 0;
      
      return {
        ...item,
        planned_seconds: planned,
        actual_seconds: actual,
        variance_seconds: variance,
        variance_percent: variancePercent,
        status: item.status,
        efficiency: planned > 0 ? (planned / Math.max(actual, 1)) * 100 : 100
      };
    });

    // Overall efficiency - cap at 100% and handle edge cases
    let overallEfficiency = 100;
    if (totalAgendaTime > 0 && totalPlannedTime > 0) {
      overallEfficiency = Math.min(((totalPlannedTime / totalAgendaTime) * 100), 100).toFixed(0);
    } else if (totalAgendaTime === 0 && totalPlannedTime > 0) {
      // Meeting hasn't started or no time tracked yet
      overallEfficiency = 'N/A';
    }

    // Items that went over time
    const itemsOverTime = itemAnalytics.filter(i => i.variance_seconds > 60); // More than 1 min over
    const itemsUnderTime = itemAnalytics.filter(i => i.variance_seconds < -60); // More than 1 min under
    const itemsOnTime = itemAnalytics.filter(i => Math.abs(i.variance_seconds) <= 60);

    setAnalyticsData({
      totalMeetingTime,
      generalAdminTime,
      totalAgendaTime,
      totalPlannedTime,
      overallVariance: totalAgendaTime - totalPlannedTime,
      overallEfficiency,
      itemAnalytics,
      itemsOverTime: itemsOverTime.length,
      itemsUnderTime: itemsUnderTime.length,
      itemsOnTime: itemsOnTime.length,
      completedItems: agendaItems.filter(i => i.status === 'completed').length,
      skippedItems: agendaItems.filter(i => i.status === 'skipped').length,
      deferredItems: agendaItems.filter(i => i.status === 'deferred').length,
    });
    
    setLoading(false);
  };

  const exportReport = () => {
    if (!analyticsData) return;
    
    // Generate text report
    const report = `
MEETING TIME ANALYTICS REPORT
=============================
Meeting: ${meeting?.title || 'Board Meeting'}
Date: ${meeting?.scheduled_date ? new Date(meeting.scheduled_date).toLocaleDateString() : 'N/A'}

SUMMARY
-------
Total Meeting Duration: ${formatDuration(analyticsData.totalMeetingTime)}
General/Admin Time: ${formatDuration(analyticsData.generalAdminTime)}
Agenda Discussion Time: ${formatDuration(analyticsData.totalAgendaTime)}

PLANNED VS ACTUAL
-----------------
Planned Duration: ${formatDuration(analyticsData.totalPlannedTime)}
Actual Duration: ${formatDuration(analyticsData.totalAgendaTime)}
Variance: ${analyticsData.overallVariance > 0 ? '+' : ''}${formatDuration(Math.abs(analyticsData.overallVariance))} ${analyticsData.overallVariance > 0 ? 'over' : 'under'}
Time Efficiency: ${analyticsData.overallEfficiency}%

AGENDA ITEM BREAKDOWN
---------------------
${analyticsData.itemAnalytics.map((item, idx) => 
  `${idx + 1}. ${item.title}
   Status: ${item.status || 'pending'}
   Planned: ${formatDuration(item.planned_seconds)} | Actual: ${formatDuration(item.actual_seconds)}
   Variance: ${item.variance_seconds > 0 ? '+' : ''}${formatDuration(Math.abs(item.variance_seconds))}
`).join('\n')}

STATISTICS
----------
Items Completed: ${analyticsData.completedItems}
Items Skipped: ${analyticsData.skippedItems}
Items Deferred: ${analyticsData.deferredItems}
Items Over Time: ${analyticsData.itemsOverTime}
Items Under Time: ${analyticsData.itemsUnderTime}
Items On Time: ${analyticsData.itemsOnTime}

Generated: ${new Date().toLocaleString()}
    `.trim();

    // Download as text file
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-time-report-${meetingId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Report downloaded');
  };

  const getVarianceColor = (variance) => {
    if (Math.abs(variance) <= 60) return 'text-green-600';
    if (variance > 0) return 'text-red-600';
    return 'text-blue-600';
  };

  const getVarianceIcon = (variance) => {
    if (Math.abs(variance) <= 60) return <Minus className="h-3 w-3" />;
    if (variance > 0) return <TrendingUp className="h-3 w-3" />;
    return <TrendingDown className="h-3 w-3" />;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'skipped':
        return <XCircle className="h-4 w-4 text-gray-400" />;
      case 'deferred':
        return <ArrowRight className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-300" />;
    }
  };

  // Calculate percentage for visual bars
  const getTimePercentage = (seconds) => {
    if (!analyticsData || analyticsData.totalMeetingTime === 0) return 0;
    return Math.min((seconds / analyticsData.totalMeetingTime) * 100, 100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            Time Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : analyticsData ? (
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Duration</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {formatDuration(analyticsData.totalMeetingTime)}
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Timer className="h-4 w-4" />
                  <span className="text-sm font-medium">Planned</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">
                  {formatDuration(analyticsData.totalPlannedTime)}
                </p>
              </div>
              
              <div className={`rounded-lg p-4 ${
                analyticsData.overallVariance > 300 ? 'bg-red-50' : 
                analyticsData.overallVariance < -300 ? 'bg-green-50' : 'bg-gray-50'
              }`}>
                <div className={`flex items-center gap-2 mb-1 ${
                  analyticsData.overallVariance > 300 ? 'text-red-600' : 
                  analyticsData.overallVariance < -300 ? 'text-green-600' : 'text-gray-600'
                }`}>
                  {analyticsData.overallVariance > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  <span className="text-sm font-medium">Variance</span>
                </div>
                <p className={`text-2xl font-bold ${
                  analyticsData.overallVariance > 300 ? 'text-red-900' : 
                  analyticsData.overallVariance < -300 ? 'text-green-900' : 'text-gray-900'
                }`}>
                  {analyticsData.overallVariance > 0 ? '+' : ''}{formatDuration(Math.abs(analyticsData.overallVariance))}
                </p>
              </div>
            </div>

            {/* Time Distribution Visual */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Time Distribution</h4>
              <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
                <div 
                  className="bg-purple-300 h-full transition-all duration-300"
                  style={{ width: `${getTimePercentage(analyticsData.generalAdminTime)}%` }}
                  title={`General/Admin: ${formatDuration(analyticsData.generalAdminTime)}`}
                />
                {analyticsData.itemAnalytics.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`h-full transition-all duration-300 ${
                      idx % 2 === 0 ? 'bg-purple-500' : 'bg-purple-600'
                    }`}
                    style={{ width: `${getTimePercentage(item.actual_seconds)}%` }}
                    title={`${item.title}: ${formatDuration(item.actual_seconds)}`}
                  />
                ))}
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-purple-300 rounded" />
                  General/Admin ({formatDuration(analyticsData.generalAdminTime)})
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-purple-500 rounded" />
                  Agenda Items ({formatDuration(analyticsData.totalAgendaTime)})
                </span>
              </div>
            </div>

            {/* Item Status Summary */}
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-700">{analyticsData.completedItems}</p>
                <p className="text-xs text-green-600">Completed</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-700">{analyticsData.skippedItems}</p>
                <p className="text-xs text-gray-600">Skipped</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-700">{analyticsData.deferredItems}</p>
                <p className="text-xs text-yellow-600">Deferred</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-700">{analyticsData.itemsOverTime}</p>
                <p className="text-xs text-red-600">Over Time</p>
              </div>
            </div>

            {/* Detailed Item Analysis */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Agenda Item Analysis</h4>
              <div className="space-y-2">
                {analyticsData.itemAnalytics.map((item, idx) => (
                  <div key={item.id} className="bg-white border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(item.status)}
                        <div>
                          <span className="font-medium text-gray-900">{idx + 1}. {item.title}</span>
                          <div className="flex gap-3 text-xs text-gray-500 mt-1">
                            <span>Planned: {formatDuration(item.planned_seconds)}</span>
                            <span>Actual: {formatDuration(item.actual_seconds)}</span>
                          </div>
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 text-sm font-medium ${getVarianceColor(item.variance_seconds)}`}>
                        {getVarianceIcon(item.variance_seconds)}
                        <span>
                          {item.variance_seconds > 0 ? '+' : ''}{formatDuration(Math.abs(item.variance_seconds))}
                        </span>
                      </div>
                    </div>
                    
                    {/* Time bar visualization */}
                    <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          item.variance_seconds > 60 ? 'bg-red-400' :
                          item.variance_seconds < -60 ? 'bg-blue-400' : 'bg-green-400'
                        }`}
                        style={{ 
                          width: `${Math.min((item.actual_seconds / Math.max(item.planned_seconds, item.actual_seconds, 1)) * 100, 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Efficiency Score */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Meeting Efficiency Score</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Based on planned vs actual time for completed items
                </p>
              </div>
              <div className={`text-3xl font-bold ${
                analyticsData.overallEfficiency === 'N/A' ? 'text-gray-400' :
                parseInt(analyticsData.overallEfficiency) >= 90 ? 'text-green-600' :
                parseInt(analyticsData.overallEfficiency) >= 70 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {analyticsData.overallEfficiency === 'N/A' ? 'N/A' : `${analyticsData.overallEfficiency}%`}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No analytics data available</p>
          </div>
        )}

        {/* Footer with Export */}
        <div className="border-t pt-4 mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={exportReport} disabled={!analyticsData}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeAnalyticsPanel;
